import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    AlertTriangle,
    BarChart3,
    BookOpen,
    CheckCircle2,
    Download,
    Edit2,
    Eye,
    FileSpreadsheet,
    ImagePlus,
    Layout,
    Plus,
    Save,
    Search,
    ShieldCheck,
    Upload,
    Users,
    X,
} from "lucide-react";

import { API_BASE_URL } from "../../../../../../api";
import {
    clearModuleCleImportResult,
    createModuleCleRequest,
    deleteModuleCleRequest,
    downloadModuleCleTemplateRequest,
    fetchModulesCleRequest,
    importModuleCleExcelRequest,
    updateModuleCleRequest,
} from "../../../../../redux/actions/modulesCleActions";
import {
    fetchFormationsByCatalogueRequest,
    fetchFormationsRequest,
} from "../../../../../redux/actions/formationsActions";
import { fetchCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import {
    getModuleCleTypeLabel,
    MODULE_CLE_TYPE_OPTIONS,
} from "../../../../contenuModule/moduleCleContentHelpers";

import "../../RessourcesStyles/ModuleCleManagement.css";
import BulkAssignmentModal from "../assignments/BulkAssignmentModal";

const STATUS_OPTIONS = [
    { value: "ACTIF", label: "Actif" },
    { value: "INACTIF", label: "Inactif" },
    { value: "ARCHIVE", label: "Archivé" },
];

const MODE_OPTIONS = [
    { value: "PRESENTIEL", label: "Présentiel" },
    { value: "DISTANCIEL", label: "Distanciel" },
    { value: "E_LEARNING", label: "E-learning" },
    { value: "HYBRIDE", label: "Hybride" },
];

const HORS_CATALOGUE_OPTION = "HORS_CATALOGUE";

const emptyForm = {
    nom: "",
    reference: "",
    description: "",
    mode: "PRESENTIEL",
    actionF: "",
    status: "ACTIF",
    type: "FORMATION",
    ordre: "",
    formationId: "",
    campagneId: "",
    catalogueId: "",
    picture: "",
    imageFile: null,
    removePicture: false,
};

const getEmptyForm = (moduleContext) => ({
    ...emptyForm,
    type: moduleContext || emptyForm.type,
});

const normalize = (value) => String(value || "").trim().toLowerCase();

const getModuleImageUrl = (filename) =>
    filename ? `${API_BASE_URL}/api/modules-cle/images/${filename}` : null;

const getStatusBadgeClass = (status) => {
    if (status === "ACTIF") return "for-status--ok";
    if (status === "ARCHIVE") return "for-status--warn";
    return "for-status--ko";
};

const getStatusLabel = (status) =>
    STATUS_OPTIONS.find((option) => option.value === status)?.label || status || "Inactif";

const getModeLabel = (mode) =>
    MODE_OPTIONS.find((option) => option.value === mode)?.label || mode || "Non défini";

const EMPTY_MODULE_ASSIGNMENT_MESSAGE =
    "Ce module ne contient aucun contenu. L’affectation des utilisateurs est désactivée.";

const IMPORT_ACTION_OPTIONS = [
    { value: "add", label: "Ajouter" },
    { value: "edit", label: "Modifier" },
];

const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;

    return (
        <div className="for-modal-overlay" onMouseDown={onClose}>
            <div className="for-modal for-modal--module" onMouseDown={(event) => event.stopPropagation()}>
                <div className="for-modal-header">
                    <h3>
                        <Layout size={18} style={{ color: "#ee645f" }} />
                        {title}
                    </h3>
                    <button className="for-icon-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="for-modal-body">{children}</div>
                {footer ? <div className="for-modal-footer">{footer}</div> : null}
            </div>
        </div>
    );
};

const ModuleDetailsModal = ({ open, moduleItem, onClose, onManageContents }) => {
    if (!open || !moduleItem) return null;

    return (
        <Modal
            open={open}
            title="Détails du module"
            onClose={onClose}
            footer={
                <>
                    <button className="for-btn for-btn-ghost" onClick={onClose}>
                        Fermer
                    </button>
                    <button className="for-btn for-btn-primary" onClick={() => onManageContents(moduleItem.id)}>
                        <BookOpen size={16} />
                        Gérer les contenus
                    </button>
                </>
            }
        >
            <div className="for-details-layout">
                <div className="for-details-hero">
                    <div className="for-details-hero__thumb">
                        {moduleItem.picture ? (
                            <img src={getModuleImageUrl(moduleItem.picture)} alt={moduleItem.nom} />
                        ) : (
                            <Layout size={26} />
                        )}
                    </div>
                    <div className="for-details-hero__content">
                        <h2>{moduleItem.nom}</h2>
                        <div className="for-details-badges">
                            <span className="for-badge">{moduleItem.reference || "Sans référence"}</span>
                            <span className="for-badge">{getModuleCleTypeLabel(moduleItem.type)}</span>
                            <span className="for-badge">{getModeLabel(moduleItem.mode)}</span>
                            <span className={`for-status ${getStatusBadgeClass(moduleItem.status)}`}>
                                <CheckCircle2 size={12} />
                                {getStatusLabel(moduleItem.status)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="for-details-grid">
                    <div className="for-detail-card">
                        <span className="for-detail-card__label">Campagne</span>
                        <strong>{moduleItem.campagneNom || "Non renseignée"}</strong>
                    </div>
                    <div className="for-detail-card">
                        <span className="for-detail-card__label">Formation / action</span>
                        <strong>{moduleItem.formationNom || moduleItem.actionF || "Non renseigné"}</strong>
                    </div>
                    <div className="for-detail-card">
                        <span className="for-detail-card__label">Ordre</span>
                        <strong>{moduleItem.type === "ONBOARDING" ? moduleItem.ordre || "Non renseigné" : "Non utilisé"}</strong>
                    </div>
                    <div className="for-detail-card">
                        <span className="for-detail-card__label">Type</span>
                        <strong>{getModuleCleTypeLabel(moduleItem.type)}</strong>
                    </div>
                </div>

                <div className="for-detail-card for-detail-card--wide">
                    <span className="for-detail-card__label">Description</span>
                    <p>{moduleItem.description || "Aucune description renseignée."}</p>
                </div>
            </div>
        </Modal>
    );
};

export default function ModuleCleManagement({ moduleContext }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const modulesState = useSelector((state) => state.modulesCle || {});
    const formationsState = useSelector((state) => state.formations || {});
    const campagnesState = useSelector((state) => state.campagnes || {});
    const cataloguesState = useSelector((state) => state.catalogues || {});

    const modules = modulesState.modulesCle || [];
    const bulkLoading = !!modulesState.bulkLoading;
    const importResult = modulesState.importResult;
    const formations = formationsState.formations || formationsState.list || [];
    const campagnes = campagnesState.campagnes || campagnesState.list || campagnesState.visibleList || [];
    const catalogues = cataloguesState.catalogues || [];

    const loading = !!(modulesState.loading || formationsState.loading || campagnesState.loading || cataloguesState.loading);

    const [searchText, setSearchText] = useState("");
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(() => getEmptyForm(moduleContext));
    const [assignmentModule, setAssignmentModule] = useState(null);
    const [detailModule, setDetailModule] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkFileName, setBulkFileName] = useState("");
    const [bulkAction, setBulkAction] = useState("add");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        dispatch(fetchModulesCleRequest());
        dispatch(fetchFormationsRequest());
        dispatch(fetchCampagnesRequest());
        dispatch(fetchCataloguesRequest());
    }, [dispatch]);

    const formationCampaigns = useMemo(
        () => campagnes.filter((campagne) => !campagne?.typeCampagne || String(campagne.typeCampagne).toUpperCase() === "FORMATION"),
        [campagnes]
    );

    const availableCatalogues = useMemo(() => {
        const selectedCampaign = formationCampaigns.find((item) => String(item.id) === String(form.campagneId));
        if (!selectedCampaign?.catalogueIds?.length) {
            return [];
        }

        const allowedIds = new Set((selectedCampaign.catalogueIds || []).map((id) => String(id)));
        return catalogues.filter((catalogue) => allowedIds.has(String(catalogue.id)));
    }, [catalogues, formationCampaigns, form.campagneId]);

    const isHorsCatalogueSelected = form.catalogueId === HORS_CATALOGUE_OPTION;
    const showFormationField = !!form.catalogueId && !isHorsCatalogueSelected;
    const showActionField = isHorsCatalogueSelected;

    const filteredModules = useMemo(() => {
        const query = normalize(searchText);
        return (modules || []).filter((moduleItem) => {
            if (moduleContext && moduleItem?.type !== moduleContext) {
                return false;
            }
            return (
                !query ||
                normalize(moduleItem?.nom).includes(query) ||
                normalize(moduleItem?.reference).includes(query) ||
                normalize(moduleItem?.formationNom).includes(query) ||
                normalize(moduleItem?.campagneNom).includes(query) ||
                normalize(moduleItem?.actionF).includes(query) ||
                normalize(moduleItem?.mode).includes(query) ||
                normalize(moduleItem?.status).includes(query)
            );
        });
    }, [modules, searchText, moduleContext]);

    const totalPages = Math.max(1, Math.ceil(filteredModules.length / itemsPerPage));
    const paginatedModules = useMemo(() => {
        return filteredModules.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [currentPage, filteredModules, itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    useEffect(() => {
        const total = Math.ceil(filteredModules.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredModules.length, currentPage, itemsPerPage]);

    useEffect(() => {
        if (!importResult || bulkLoading) return;

        if (importResult.dryRun) {
            setBulkOpen(false);
            setPreviewOpen(true);
            return;
        }

        setPreviewOpen(false);
        setBulkOpen(false);
        setBulkFile(null);
        setBulkFileName("");
        dispatch(fetchModulesCleRequest());
    }, [bulkLoading, dispatch, importResult]);

    const previewUrl = useMemo(() => {
        if (form.imageFile) {
            return URL.createObjectURL(form.imageFile);
        }
        if (!form.removePicture && form.picture) {
            return getModuleImageUrl(form.picture);
        }
        return null;
    }, [form.imageFile, form.picture, form.removePicture]);

    useEffect(() => {
        return () => {
            if (form.imageFile && previewUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [form.imageFile, previewUrl]);

    const openAdd = () => {
        setOpen(true);
        setIsEdit(false);
        setCurrentId(null);
        setForm(getEmptyForm(moduleContext));
    };

    const openBulk = () => {
        setBulkOpen(true);
        setPreviewOpen(false);
        setBulkFile(null);
        setBulkFileName("");
        setBulkAction("add");
        dispatch(clearModuleCleImportResult());
    };

    const openEdit = (moduleItem) => {
        setOpen(true);
        setIsEdit(true);
        setCurrentId(moduleItem.id);
        setForm({
            nom: moduleItem.nom || "",
            reference: moduleItem.reference || "",
            description: moduleItem.description || "",
            mode: moduleItem.mode || "PRESENTIEL",
            actionF: moduleItem.actionF || "",
            status: moduleItem.status || "ACTIF",
            type: moduleItem.type || emptyForm.type,
            ordre: moduleItem.ordre ? String(moduleItem.ordre) : "",
            formationId: String(moduleItem.formationId || ""),
            campagneId: String(moduleItem.campagneId || ""),
            catalogueId: "",
            picture: moduleItem.picture || "",
            imageFile: null,
            removePicture: false,
        });
    };
    const closeModal = () => {
        setOpen(false);
        setForm(getEmptyForm(moduleContext));
    };

    const closeBulkModal = () => {
        setBulkOpen(false);
        setBulkFile(null);
        setBulkFileName("");
    };

    const openDetails = (moduleItem) => setDetailModule(moduleItem);
    const closeDetails = () => setDetailModule(null);

    const runBulkDryRun = () => {
        if (!bulkFile) {
            Swal.fire({ icon: "warning", text: "Veuillez selectionner un fichier Excel.", timer: 1800, showConfirmButton: false });
            return;
        }
        dispatch(importModuleCleExcelRequest(bulkFile, { dryRun: true, action: bulkAction }));
    };

    const confirmBulkImport = () => {
        if (!bulkFile) return;
        setPreviewOpen(false);
        dispatch(importModuleCleExcelRequest(bulkFile, { dryRun: false, action: bulkAction }));
    };

    const updateForm = (field, value) => {
        setForm((previous) => {
            if (field === "type") {
                return {
                    ...previous,
                    type: value,
                    ordre: value === "ONBOARDING" ? previous.ordre : "",
                    campagneId: value === "FORMATION" ? previous.campagneId : "",
                    catalogueId: value === "FORMATION" ? previous.catalogueId : "",
                    formationId: value === "FORMATION" ? previous.formationId : "",
                    actionF: value === "FORMATION" ? previous.actionF : "",
                };
            }

            if (field === "campagneId") {
                return {
                    ...previous,
                    campagneId: value,
                    catalogueId: "",
                    formationId: "",
                    actionF: "",
                };
            }

            if (field === "catalogueId") {
                if (value === HORS_CATALOGUE_OPTION) {
                    return {
                        ...previous,
                        catalogueId: value,
                        formationId: "",
                        actionF: "",
                    };
                }
                return {
                    ...previous,
                    catalogueId: value,
                    formationId: "",
                    actionF: "",
                };
            }

            if (field === "formationId" && value) {
                return {
                    ...previous,
                    formationId: value,
                    actionF: "",
                };
            }

            return { ...previous, [field]: value };
        });
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0] || null;
        setForm((previous) => ({
            ...previous,
            imageFile: file,
            removePicture: false,
        }));
    };

    const clearImage = () => {
        setForm((previous) => ({
            ...previous,
            imageFile: null,
            removePicture: true,
        }));
    };

    useEffect(() => {
        if (!form.campagneId || !form.catalogueId || form.catalogueId === HORS_CATALOGUE_OPTION) {
            return;
        }
        dispatch(fetchFormationsByCatalogueRequest(form.catalogueId));
    }, [dispatch, form.campagneId, form.catalogueId]);

    const validate = () => {
        if (!form.nom.trim()) return "Le nom du module est obligatoire.";
        if (!form.mode) return "Le mode est obligatoire.";
        if (!form.status) return "Le statut est obligatoire.";
        
        if (form.type === "FORMATION") {
            if (!form.campagneId) return "La campagne est obligatoire.";
            if (!form.catalogueId) return "Le catalogue est obligatoire.";
            if (isHorsCatalogueSelected && !form.actionF.trim()) {
                return "L'action de formation est obligatoire pour un choix hors catalogue.";
            }
            if (!isHorsCatalogueSelected && !form.formationId) {
                return "La formation est obligatoire pour le catalogue sélectionné.";
            }
        }

        if (form.type === "ONBOARDING" && (!form.ordre || Number(form.ordre) <= 0)) {
            return "L'ordre est obligatoire pour un module d'onboarding.";
        }
        return null;
    };

    const submit = () => {
        const error = validate();
        if (error) {
            Swal.fire({ icon: "warning", text: error, timer: 1800, showConfirmButton: false });
            return;
        }

        const isFormation = form.type === "FORMATION";
        const isOndboarding = form.type === "ONBOARDING";
        const payload = {
            nom: form.nom.trim(),
            reference: form.reference.trim() || null,
            description: form.description.trim() || null,
            mode: form.mode,
            actionF: isFormation && isHorsCatalogueSelected ? form.actionF.trim() || null : null,
            status: form.status,
            type: form.type,
            ordre: isOndboarding ? Number(form.ordre) : null,
            formationId: isFormation && !isHorsCatalogueSelected && form.formationId ? Number(form.formationId) : null,
            campagneId: isFormation && form.campagneId ? Number(form.campagneId) : null,
            removePicture: !!form.removePicture,
            imageFile: form.imageFile || null,
        };

        if (isEdit && currentId) {
            dispatch(updateModuleCleRequest(currentId, payload));
        } else {
            dispatch(createModuleCleRequest(payload));
        }

        closeModal();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Supprimer ce module ?",
            text: "Cette action est irréversible.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteModuleCleRequest(id));
            }
        });
    };

    return (
        <div className="for-page module-management-page">
            <div className="for-header">
                <div>
                    <h1>Gestion des modules</h1>
                </div>
                <div className="for-toolbar-actions">
                    <button className="for-btn for-btn-ghost" onClick={() => dispatch(downloadModuleCleTemplateRequest())} disabled={bulkLoading}>
                        <FileSpreadsheet size={16} />
                        Télécharger template
                    </button>
                    <button className="for-btn for-btn-ghost" onClick={openBulk} disabled={bulkLoading}>
                        <Download size={16} />
                        Importer Excel
                    </button>
                    <button className="for-btn for-btn-primary module-add-btn" onClick={openAdd}>
                        <Plus size={16} />
                        Ajouter un module
                    </button>
                </div>
            </div>

            <div className="for-toolbar">
                <div className="for-search-wrap">
                    <Search size={15} className="for-search-icon" />
                    <input
                        className="for-search"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        placeholder="Rechercher par nom, référence, campagne ou formation..."
                    />
                </div>

            </div>

            <div className="for-card">
                <div className="for-table-wrap">
                    <table className="for-table">
                        <thead>
                            <tr>
                                <th>Référence</th>
                                <th>Nom</th>
                                <th>Image</th>
                                <th>Mode</th>
                                <th>Statut</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredModules.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: "center", padding: 48, color: "#94a3b8" }}>
                                        {loading ? "Recherche en cours..." : "Aucun module défini."}
                                    </td>
                                </tr>
                            ) : (
                                paginatedModules.map((moduleItem) => (
                                    <tr key={moduleItem.id}>
                                        <td>
                                            {moduleItem.reference}
                                        </td>
                                        <td>
                                            {moduleItem.nom}
                                        </td>

                                        <td>

                                            <div className="for-module-cell__thumb">
                                                {moduleItem.picture ? (
                                                    <img src={getModuleImageUrl(moduleItem.picture)} alt={moduleItem.nom} />
                                                ) : (
                                                    <Layout size={16} />
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="for-badge">{getModeLabel(moduleItem.mode)}</span>
                                        </td>
                                        <td>
                                            <span className={`for-status ${getStatusBadgeClass(moduleItem.status)}`}>
                                                <CheckCircle2 size={12} />
                                                {getStatusLabel(moduleItem.status)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div className="for-actions-row">
                                                <button className="for-icon-btn" title="Voir les détails" onClick={() => openDetails(moduleItem)}>
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    className="for-icon-btn"
                                                    title="Gérer les contenus du module"
                                                    onClick={() => navigate(`/formation/responsable/modules/${moduleItem.id}/contents`)}
                                                >
                                                    <BookOpen size={16} />
                                                </button>
                                                <button
                                                    className="for-icon-btn"
                                                    title="Reporting SCORM"
                                                    onClick={() => navigate(`/formation/responsable/modules/${moduleItem.id}/scorm/progress`)}
                                                >
                                                    <BarChart3 size={16} />
                                                </button>
                                                <button
                                                    className="for-icon-btn"
                                                    title="Reporting LMS"
                                                    onClick={() => navigate(`/formation/responsable/modules/${moduleItem.id}/lms/reporting`)}
                                                >
                                                    <ShieldCheck size={16} />
                                                </button>
                                                <button className="for-icon-btn" title="Modifier" onClick={() => openEdit(moduleItem)}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <span title={!moduleItem.hasContents ? EMPTY_MODULE_ASSIGNMENT_MESSAGE : "Affecter des utilisateurs"}>
                                                    <button
                                                        className="for-icon-btn"
                                                        title={!moduleItem.hasContents ? EMPTY_MODULE_ASSIGNMENT_MESSAGE : "Affecter des utilisateurs"}
                                                        onClick={() => setAssignmentModule(moduleItem)}
                                                        disabled={!moduleItem.hasContents}
                                                    >
                                                        <Users size={16} />
                                                    </button>
                                                </span>
                                                <button
                                                    className="for-icon-btn for-icon-btn--danger"
                                                    title="Supprimer"
                                                    onClick={() => handleDelete(moduleItem.id)}
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filteredModules.length > 0 ? (
                    <div className="modulecle-pagination">
                        <div className="modulecle-pagination-info">
                            Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredModules.length)} sur {filteredModules.length} modules
                        </div>
                        <div className="modulecle-pagination-actions">
                            <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                                Précédent
                            </button>
                            <span>{`Page ${currentPage} / ${totalPages}`}</span>
                            <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>
                                Suivant
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>

            <BulkAssignmentModal
                open={!!assignmentModule}
                onClose={() => setAssignmentModule(null)}
                assignmentType="MODULE"
                targetId={assignmentModule?.id}
                targetLabel={assignmentModule?.nom}
                assignmentDisabled={assignmentModule?.hasContents === false}
                assignmentDisabledMessage={EMPTY_MODULE_ASSIGNMENT_MESSAGE}
            />

            <ModuleDetailsModal
                open={!!detailModule}
                moduleItem={detailModule}
                onClose={closeDetails}
                onManageContents={(id) => {
                    closeDetails();
                    navigate(`/formation/responsable/modules/${id}/contents`);
                }}
            />

            <Modal
                open={bulkOpen}
                title="Importer des modules cles"
                onClose={closeBulkModal}
                footer={
                    <>
                        <button className="for-btn for-btn-ghost" onClick={closeBulkModal}>
                            Annuler
                        </button>
                        <button className="for-btn for-btn-primary" onClick={runBulkDryRun} disabled={bulkLoading}>
                            <Upload size={16} />
                            {bulkLoading ? "Analyse..." : "Lancer la previsualisation"}
                        </button>
                    </>
                }
            >
                <div className="for-form-layout">
                    <section className="for-form-panel">
                        <div className="for-form-section">
                            <div className="for-form-title">Fichier Excel</div>
                            <div className="for-grid">
                                <div className="for-field">
                                    <label>Action d'import *</label>
                                    <select value={bulkAction} onChange={(event) => setBulkAction(event.target.value)}>
                                        {IMPORT_ACTION_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="for-field for-field--full">
                                    <label>Fichier *</label>
                                    <label className="for-upload-btn" style={{ width: "fit-content" }}>
                                        <Upload size={14} />
                                        Choisir un fichier Excel
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            hidden
                                            onChange={(event) => {
                                                const file = event.target.files?.[0] || null;
                                                setBulkFile(file);
                                                setBulkFileName(file?.name || "");
                                            }}
                                        />
                                    </label>
                                    <div className="for-inline-note">
                                        {bulkFileName || "Aucun fichier selectionne."}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <aside className="for-side-panel">
                        <div className="for-form-section">
                            <div className="for-form-title">Conseils</div>
                            <div className="for-side-help">
                                <AlertTriangle size={16} />
                                <div>
                                    <strong>Verification avant création réelle</strong>
                                    <p>Le fichier passe d'abord en dry-run pour afficher les lignes valides et les erreurs ligne par ligne.</p>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </Modal>

            <Modal
                open={previewOpen}
                title="Previsualisation import Excel"
                onClose={() => setPreviewOpen(false)}
                footer={
                    <>
                        <button className="for-btn for-btn-ghost" onClick={() => setPreviewOpen(false)}>
                            Fermer
                        </button>
                        <button
                            className="for-btn for-btn-primary"
                            onClick={confirmBulkImport}
                            disabled={bulkLoading || !importResult?.preview?.length}
                        >
                            <Save size={16} />
                            Confirmer l'import
                        </button>
                    </>
                }
            >
                <div className="for-form-section">
                    <div className="for-form-title">Synthese</div>
                    <div className="for-details-grid">
                        <div className="for-detail-card">
                            <span className="for-detail-card__label">Total lignes</span>
                            <strong>{importResult?.totalRows || 0}</strong>
                        </div>
                        <div className="for-detail-card">
                            <span className="for-detail-card__label">A creer</span>
                            <strong>{importResult?.createdCount || 0}</strong>
                        </div>
                        <div className="for-detail-card">
                            <span className="for-detail-card__label">A modifier</span>
                            <strong>{importResult?.updatedCount || 0}</strong>
                        </div>
                        <div className="for-detail-card">
                            <span className="for-detail-card__label">Lignes en erreur</span>
                            <strong>{importResult?.skippedCount || 0}</strong>
                        </div>
                    </div>
                </div>

                <div className="for-form-section">
                    <div className="for-form-title">Lignes valides</div>
                    <div className="for-table-wrap">
                        <table className="for-table">
                            <thead>
                                <tr>
                                    <th>Reference</th>
                                    <th>Nom</th>
                                    <th>Campagne</th>
                                    <th>Formation / action</th>
                                    <th>Statut</th>
                                    <th>Action prevue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!importResult?.preview?.length ? (
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>
                                            Aucune ligne valide.
                                        </td>
                                    </tr>
                                ) : (
                                    importResult.preview.map((item, index) => (
                                        <tr key={`${item.reference || "sans-reference"}-${index}`}>
                                            <td>{item.reference || "Sans reference"}</td>
                                            <td>{item.nom}</td>
                                            <td>{item.campagneReference || item.campagneNom || "-"}</td>
                                            <td>{item.formationReference || item.actionF || "-"}</td>
                                            <td>{item.status || "-"}</td>
                                            <td>{item.plannedAction || "-"}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="for-form-section">
                    <div className="for-form-title">Erreurs</div>
                    <div className="for-table-wrap">
                        <table className="for-table">
                            <thead>
                                <tr>
                                    <th>Ligne</th>
                                    <th>Champ</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!importResult?.errors?.length ? (
                                    <tr>
                                        <td colSpan={3} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>
                                            Aucune erreur bloquante.
                                        </td>
                                    </tr>
                                ) : (
                                    importResult.errors.map((error, index) => (
                                        <tr key={`${error.row}-${error.field}-${index}`}>
                                            <td>{error.row}</td>
                                            <td>{error.field}</td>
                                            <td>{error.message}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            <Modal
                open={open}
                title={isEdit ? "Modifier le module" : "Ajouter un module"}
                onClose={closeModal}
                footer={
                    <>
                        <button className="for-btn for-btn-ghost" onClick={closeModal}>
                            Annuler
                        </button>
                        <button className="for-btn for-btn-primary" onClick={submit}>
                            <Save size={16} />
                            {loading ? "Traitement..." : "Enregistrer"}
                        </button>
                    </>
                }
            >
                <div className="for-form-layout">
                    <section className="for-form-panel">
                        <div className="for-form-section">
                            <div className="for-form-title">Identification</div>
                            <div className="for-grid">
                                <div className="for-field">
                                    <label>Nom du module *</label>
                                    <input
                                        placeholder="Ex : Parcours d'intégration"
                                        value={form.nom}
                                        onChange={(event) => updateForm("nom", event.target.value)}
                                    />
                                </div>

                                <div className="for-field">
                                    <label>Référence</label>
                                    <input
                                        placeholder="Ex : MOD-ONB-001"
                                        value={form.reference}
                                        onChange={(event) => updateForm("reference", event.target.value)}
                                    />
                                </div>

                                <div className="for-field for-field--full">
                                    <label>Description</label>
                                    <textarea
                                        rows={4}
                                        placeholder="Objectifs, contexte, remarques..."
                                        value={form.description}
                                        onChange={(event) => updateForm("description", event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="for-form-section">
                            <div className="for-form-title">Structure du module</div>
                            <div className="for-grid">
                                <div className="for-field">
                                    <label>Type du module *</label>
                                    <select value={form.type} onChange={(event) => updateForm("type", event.target.value)}>
                                        {MODULE_CLE_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="for-field">
                                    <label>Statut *</label>
                                    <select value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
                                        {STATUS_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="for-field">
                                    <label>Mode du module *</label>
                                    <select value={form.mode} onChange={(event) => updateForm("mode", event.target.value)}>
                                        {MODE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {form.type === "ONBOARDING" && (
                                    <div className="for-field">
                                        <label>Ordre d'onboarding *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={form.ordre}
                                            onChange={(event) => updateForm("ordre", event.target.value)}
                                        />
                                    </div>
                                )}

                                {form.type === "FORMATION" && (
                                    <div className="for-field">
                                        <label>Campagne *</label>
                                        <select value={form.campagneId} onChange={(event) => updateForm("campagneId", event.target.value)}>
                                            <option value="">- Choisir la campagne de formation -</option>
                                            {formationCampaigns.map((campagne) => (
                                                <option key={campagne.id} value={String(campagne.id)}>
                                                    {campagne.nom || campagne.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {form.type === "FORMATION" && (
                                    <>
                                        <div className="for-field">
                                            <label>Catalogue de recherche *</label>
                                            <select
                                                value={form.catalogueId}
                                                onChange={(event) => updateForm("catalogueId", event.target.value)}
                                                disabled={!form.campagneId}
                                            >
                                                <option value="">- Choisir le catalogue -</option>
                                                <option value={HORS_CATALOGUE_OPTION}>Hors catalogue</option>
                                                {availableCatalogues.map((catalogue) => (
                                                    <option key={catalogue.id} value={String(catalogue.id)}>
                                                        {catalogue.nom || catalogue.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {showFormationField && (
                                            <div className="for-field">
                                                <label>Choix final de la formation *</label>
                                                <select value={form.formationId} onChange={(event) => updateForm("formationId", event.target.value)}>
                                                    <option value="">- Choisir la formation -</option>
                                                    {formations.map((formation) => (
                                                        <option key={formation.id} value={String(formation.id)}>
                                                            {formation.nom || formation.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {showActionField && (
                                            <div className="for-field for-field--full">
                                                <label>Action de formation *</label>
                                                <textarea
                                                    rows={3}
                                                    placeholder="Saisissez l'action de formation hors catalogue"
                                                    value={form.actionF}
                                                    onChange={(event) => updateForm("actionF", event.target.value)}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </section>

                    <aside className="for-side-panel">
                        <div className="for-form-section">
                            <div className="for-form-title">Image du module</div>
                            <div className="for-picture-card">
                                <div className="for-picture-preview">
                                    {previewUrl ? (
                                        <img src={previewUrl} alt="Aperçu du module" />
                                    ) : (
                                        <div className="for-picture-empty">
                                            <ImagePlus size={18} />
                                            <span>Aucune image</span>
                                        </div>
                                    )}
                                </div>

                                <label className="for-upload-btn">
                                    <Upload size={14} />
                                    Choisir une image
                                    <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                                </label>

                                {(form.picture || form.imageFile) && (
                                    <button type="button" className="for-btn for-btn-ghost for-btn--full" onClick={clearImage}>
                                        Retirer l'image
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="for-form-section">
                            <div className="for-form-title">Contenus du module</div>
                            <div className="for-side-help">
                                <ShieldCheck size={16} />
                                <div>
                                    <strong>Ajouter du contenu au module</strong>
                                    <p>Enregistrez d'abord la fiche, puis utilisez la page dédiée pour gérer les contenus et leur ordre.</p>
                                </div>
                            </div>

                            {isEdit && currentId ? (
                                <button
                                    type="button"
                                    className="for-btn for-btn-primary for-btn--full"
                                    onClick={() => navigate(`/formation/responsable/modules/${currentId}/contents`)}
                                >
                                    <BookOpen size={16} />
                                    Ajouter un contenu au module
                                </button>
                            ) : (
                                <div className="for-inline-note">
                                    Le bouton d'ajout de contenu sera disponible après la création du module.
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </Modal>
        </div>
    );
}
